import styled, { keyframes } from "styled-components";

// ============ Animations ============
export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============ Styled Components ============
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 10, 15, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 40px 0;
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 20px 0;
    border-radius: 16px;
    max-height: 90vh;
  }
`;

export const ModalHeader = styled.div`
  flex-shrink: 0;
  padding-bottom: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-bottom: 16px;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0 40px;
  margin: 0 -40px;
  padding-left: 40px;
  padding-right: 40px;

  /* Light Scrollbar for Modal */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin: 0 -20px;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 20px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

export const ModalTitle = styled.h2`
  font-family: inherit;
  font-size: 1.5rem;
  color: #0f172a;
  margin: 0 0 12px;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.5px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const ModalSubtitle = styled.p`
  font-family: inherit;
  color: #64748b;
  margin: 0 0 32px;
  font-size: 1rem;
  text-align: center;
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin: 0 0 24px;
  }
`;

export const PhoneInputWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

export const PhonePrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  pointer-events: none;
  z-index: 10;
`;

export const PhoneInput = styled.input`
  width: 100%;
  padding: 16px 16px 16px 60px;
  font-size: 1.125rem;
  letter-spacing: 1px;
  color: #0f172a;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-family: inherit;
  direction: ltr;
  text-align: right;
  background: #f8fafc;

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    background: #ffffff;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
    letter-spacing: normal;
  }
`;

// Modern styled button to replace generic Button
export const ModernButton = styled.button`
  width: 100%;
  background: ${(props) =>
    props.$secondary
      ? "#f1f5f9"
      : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"};
  color: ${(props) => (props.$secondary ? "#475569" : "white")};
  border: none;
  padding: 16px;
  font-family: inherit;
  font-weight: 600;
  border-radius: 12px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.$secondary
      ? "none"
      : "0 4px 6px -1px rgba(37, 99, 235, 0.2), 0 2px 4px -1px rgba(37, 99, 235, 0.1)"};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${(props) => (props.$marginTop ? props.$marginTop : "0")};

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$secondary
        ? "#e2e8f0"
        : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"};
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$secondary
        ? "none"
        : "0 10px 15px -3px rgba(37, 99, 235, 0.3), 0 4px 6px -2px rgba(37, 99, 235, 0.15)"};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 14px;
    font-size: 0.95rem;
  }
`;

export const OtpContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: 24px;
    margin-bottom: 24px;
  }
`;

export const OtpInputs = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  direction: ltr;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 8px;
  }
`;

export const OtpInput = styled.input`
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 1.5rem;
  font-family: inherit;
  font-weight: 700;
  color: #0f172a;
  border: 2px solid ${(props) => (props.$filled ? "#3b82f6" : "#e2e8f0")};
  border-radius: 12px;
  transition: all 0.2s ease;
  background: ${(props) => (props.$filled ? "#eff6ff" : "#f8fafc")};

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 42px;
    height: 52px;
    font-size: 1.25rem;
    border-radius: 10px;
  }
`;

export const ResendLink = styled.div`
  text-align: center;
  margin-top: 24px;

  button {
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-size: 0.9rem;
    font-family: inherit;
    font-weight: 500;
    transition: color 0.2s;
    padding: 8px 16px;
    border-radius: 6px;

    &:hover:not(:disabled) {
      background: #eff6ff;
      color: #2563eb;
    }

    &:disabled {
      color: #94a3b8;
      cursor: not-allowed;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    margin-top: 16px;

    button {
      font-size: 0.85rem;
      padding: 6px 12px;
    }
  }
`;

// ============ Contracts List Styles ============
export const ContractsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

export const ContractCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const ContractHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
`;

export const ContractName = styled.h3`
  font-family: inherit;
  font-size: 1.125rem;
  color: #0f172a;
  margin: 0;
  font-weight: 600;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-family: inherit;
  font-weight: 600;
  border-radius: 9999px;
  background: ${({ $status, theme }) =>
    $status === "VIEWED" ? "#dcfce7" : "#fef3c7"};
  color: ${({ $status, theme }) =>
    $status === "VIEWED" ? "#166534" : "#b45309"};
`;

export const ContractDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  font-size: 0.9rem;
`;

export const DetailItem = styled.div`
  span:first-child {
    font-family: inherit;
    color: #64748b;
    display: block;
    margin-bottom: 4px;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  span:last-child {
    font-family: inherit;
    color: #334155;
    font-weight: 500;
    font-size: 0.95rem;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
`;

export const LoadingText = styled.p`
  color: #64748b;
  font-family: inherit;
  font-size: 1rem;
  margin: 0;
`;
